// ============================================================
// User-management data layer — minimal Firestore wrapper around
// the /users/{uid} collection. Two responsibilities:
//
//   1. ensureUserDoc(user) — called on every sign-in. Creates the
//      user's doc if missing (defaulting role: 'staff'). Existing
//      docs are NOT touched (so a director can promote someone to
//      'admin' and the next sign-in won't reset them).
//
//   2. subscribeUsers / updateUserRole — used by the User Management
//      panel under Admin > Settings.
//
// auth-security note: writes are gated by Firestore Rules:
//   - any signed-in CCY user can CREATE their own /users/{uid}
//   - the user themselves and a director can READ/UPDATE
//   - role-change writes by self are guarded in the rule (see below)
//
// We also intentionally read role from this doc as a FALLBACK to
// the Firebase Auth custom claim — covers the case where a director
// promotes a user via the UI (claim setting requires Cloud Functions
// → Blaze plan, which is deferred).
//
// R1 multi-tenancy (2026-04-23): /users/{uid} stays TOP-LEVEL —
// not subcollection-scoped under a tenant. Firebase Auth users are
// platform-global (one auth pool per project). Each user doc carries
// a `tenantId` field for the user's home tenant; future multi-tenant
// users will get a `tenants[]` membership array.
// ============================================================

import {
  collection, query, where, orderBy, onSnapshot, getDoc, setDoc, updateDoc,
  doc, serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase.js';
import { DEFAULT_TENANT_ID } from './platform/tenant.js';

export const ROLES = ['director', 'admin', 'hr', 'staff', 'none'];

/**
 * Ensure /users/{uid} exists for the given Auth user. Idempotent.
 *  - missing doc → CREATE with role 'staff' + tenantId default (or what rules allow on first write)
 *  - existing doc → refresh displayName/email/photoURL (NEVER touch role or tenantId)
 *
 * tenantId on first-create defaults to DEFAULT_TENANT_ID (ccy-engineering).
 * The set-tenant.js script can promote/move users between tenants later
 * (cross-tenant onboarding flow lands in R4+).
 */
export async function ensureUserDoc(user) {
  if (!user || !user.uid) return null;
  const ref = doc(db, 'users', user.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      uid:         user.uid,
      email:       user.email || '',
      displayName: user.displayName || '',
      photoURL:    user.photoURL || '',
      role:        'staff',          // Default — director promotes via Admin UI
      tenantId:    DEFAULT_TENANT_ID,// R1: default tenant for bootstrap
      CreatedAt:   serverTimestamp(),
      UpdatedAt:   serverTimestamp()
    });
    return { uid: user.uid, role: 'staff', tenantId: DEFAULT_TENANT_ID, created: true };
  }
  // Refresh non-role profile fields if changed (cheap; no harm if same).
  const data = snap.data();
  const profileChanged =
    data.email       !== (user.email       || '') ||
    data.displayName !== (user.displayName || '') ||
    data.photoURL    !== (user.photoURL    || '');
  if (profileChanged) {
    await updateDoc(ref, {
      email:       user.email || '',
      displayName: user.displayName || '',
      photoURL:    user.photoURL || '',
      UpdatedAt:   serverTimestamp()
    });
  }
  return { uid: user.uid, role: data.role || 'staff', created: false };
}

/**
 * Read the role for a user from /users/{uid}.
 * Used as a fallback when no Auth custom claim is set yet.
 */
export async function readUserRole(uid) {
  if (!uid) return null;
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return null;
  return snap.data().role || null;
}

/**
 * Subscribe to all user docs. Used by the User Management panel.
 * Director/HR-only per Firestore Rules.
 */
export function subscribeUsers(onChange) {
  return onSnapshot(
    query(collection(db, 'users'), orderBy('email')),
    (snap) => {
      const users = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      onChange(users, snap.metadata);
    },
    (err) => console.error('[users subscribe] error:', err)
  );
}

/**
 * Set a user's role. Director-only enforcement is in Firestore Rules
 * (a non-director writing role !== their own current role is rejected).
 */
export async function updateUserRole(uid, newRole, callerUser) {
  if (!ROLES.includes(newRole)) throw new Error('Invalid role: ' + newRole);
  await updateDoc(doc(db, 'users', uid), {
    role:        newRole,
    UpdatedBy:   callerUser?.email || '',
    UpdatedAt:   serverTimestamp()
  });
  return { ok: true };
}

// ─────────────────────────────────────────────────────────────
// Phase 5C — username-auth helpers
// ─────────────────────────────────────────────────────────────

/** Fetch the user doc (one-shot, not subscribed). */
export async function fetchUserDoc(uid) {
  if (!uid) return null;
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Subscribe to /users/{uid} — main.js uses this so the app reacts
 * live when admin resets the user's password (forcePasswordChange flips).
 */
export function subscribeUserDoc(uid, onChange) {
  if (!uid) return () => {};
  return onSnapshot(
    doc(db, 'users', uid),
    (snap) => {
      if (snap.exists()) onChange({ id: snap.id, ...snap.data() });
      else onChange(null);
    },
    (err) => console.error('[subscribeUserDoc] error:', err)
  );
}

/**
 * Clear the forcePasswordChange flag — called by the force-change screen
 * after the user sets a new password. Rules allow the user themselves to
 * do this provided they change ONLY this field.
 */
export async function clearForcePasswordChange(uid) {
  await updateDoc(doc(db, 'users', uid), {
    forcePasswordChange: false
  });
  return { ok: true };
}

/** Subscribe to the subset of /users who are username-auth staff. Admin-only (rules). */
export function subscribeStaffLogins(onChange) {
  return onSnapshot(
    query(
      collection(db, 'users'),
      where('loginType', '==', 'username'),
      orderBy('username')
    ),
    (snap) => onChange(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    (err) => console.error('[subscribeStaffLogins] error:', err)
  );
}
